import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Sparkles, ArrowRight, Grid3X3, Palette, 
  Star, ImageIcon, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import Price from '../components/Price';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Featured collection visuals
const COLLECTION_VISUALS = {
  peaceful_mirror: {
    gradient: 'from-blue-50 via-slate-50 to-stone-100',
    accent: 'text-blue-700',
    badge: 'bg-blue-100 text-blue-800'
  },
  quiet_power: {
    gradient: 'from-amber-50 via-stone-50 to-rose-50',
    accent: 'text-amber-700',
    badge: 'bg-amber-100 text-amber-800'
  },
  luxury_interior: {
    gradient: 'from-stone-100 via-stone-50 to-amber-50',
    accent: 'text-stone-700',
    badge: 'bg-stone-200 text-stone-800'
  }
};

export default function WallpaperCollectionsPage() {
  const [collections, setCollections] = useState([]);
  const [allWallpapers, setAllWallpapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [collectionsRes, wallpapersRes, categoriesRes] = await Promise.all([
        api.get('/wallpaper/collections').catch(() => ({ data: { collections: [] } })),
        api.get('/wallpaper/products?limit=12').catch(() => ({ data: { products: [] } })),
        api.get('/wallpaper/categories').catch(() => ({ data: { categories: [] } }))
      ]);
      
      setCollections(collectionsRes.data.collections || []);
      setAllWallpapers(wallpapersRes.data.products || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (err) {
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const getImageSrc = (img) => {
    if (!img) return null;
    return img.startsWith('/api/') ? `${BACKEND_URL}${img}` : img;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-muted-foreground">Loading collections...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white" data-testid="wallpaper-collections-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="absolute inset-0 bg-gradient-to-br from-stone-100 via-amber-50/30 to-rose-50/20" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-amber-100/20 to-transparent" />
        
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <Badge className="mb-6 bg-white/80 text-stone-700 backdrop-blur-sm border-stone-200">
            <Sparkles size={12} className="mr-1" /> Premium Wallpaper Collections
          </Badge>
          
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-stone-900 mb-6">
            Transform Your Space
          </h1>
          
          <p className="text-lg text-stone-600 max-w-2xl mx-auto mb-10">
            Discover curated wallpaper collections designed for luxury interiors. 
            From peaceful mirrors to bold statements.
          </p>
          
          <div className="flex flex-wrap justify-center gap-3">
            {categories.slice(0, 4).map((cat, i) => (
              <Link 
                key={cat.id}
                to={`/marketplace?category=${cat.id}`}
                className="px-4 py-2 bg-white/80 hover:bg-white rounded-full border border-stone-200 text-sm text-stone-700 hover:text-amber-700 transition-colors"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Collections */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-2">Curated For You</p>
            <h2 className="font-serif text-2xl md:text-3xl text-stone-900">Featured Collections</h2>
          </div>
        </div>

        <div className="space-y-16">
          {collections.map((collection, idx) => {
            const visual = COLLECTION_VISUALS[collection.id] || COLLECTION_VISUALS.luxury_interior;
            
            return (
              <div 
                key={collection.id}
                className={`rounded-2xl overflow-hidden bg-gradient-to-br ${visual.gradient}`}
                data-testid={`collection-${collection.id}`}
              >
                <div className="p-8 md:p-12">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
                    <div>
                      <Badge className={`mb-3 ${visual.badge}`}>
                        {collection.mood}
                      </Badge>
                      <h3 className="font-serif text-2xl md:text-3xl text-stone-900 mb-2">
                        {collection.name}
                      </h3>
                      <p className="text-stone-600 max-w-lg">
                        {collection.description}
                      </p>
                    </div>
                    <Link to={`/marketplace?collection=${collection.id}`}>
                      <Button 
                        variant="outline" 
                        className="rounded-full border-stone-300 hover:bg-white"
                      >
                        View Collection <ArrowRight size={14} className="ml-2" />
                      </Button>
                    </Link>
                  </div>

                  {/* Collection Products Grid */}
                  {collection.products?.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {collection.products.slice(0, 4).map((product, i) => {
                        const imgSrc = getImageSrc(product.images?.[0]);
                        return (
                          <Link 
                            key={product.id}
                            to={product.is_wallpaper ? `/wallpaper/${product.id}` : `/products/${product.id}`}
                            className="group"
                          >
                            <div className="aspect-[4/3] rounded-lg overflow-hidden bg-white shadow-sm group-hover:shadow-lg transition-all">
                              {imgSrc ? (
                                <img 
                                  src={imgSrc} 
                                  alt={product.title}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-stone-100">
                                  <ImageIcon size={32} className="text-stone-300" />
                                </div>
                              )}
                            </div>
                            <div className="mt-3">
                              <p className="text-sm font-medium text-stone-800 truncate group-hover:text-amber-700 transition-colors">
                                {product.title}
                              </p>
                              <p className="text-sm text-stone-500"><Price amountEur={product.price} /></p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-stone-500">
                      <Palette size={32} className="mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Coming soon to this collection</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* All Wallpapers Grid */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-2">Browse</p>
            <h2 className="font-serif text-2xl md:text-3xl text-stone-900">All Wallpapers</h2>
          </div>
          <Link to="/marketplace?category=wallpaper">
            <Button variant="ghost" className="text-amber-700 hover:text-amber-800">
              View All <ChevronRight size={16} />
            </Button>
          </Link>
        </div>

        {allWallpapers.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {allWallpapers.map((product) => {
              const imgSrc = getImageSrc(product.images?.[0]);
              return (
                <Link 
                  key={product.id}
                  to={product.is_wallpaper ? `/wallpaper/${product.id}` : `/products/${product.id}`}
                  className="group"
                  data-testid={`wallpaper-card-${product.id}`}
                >
                  <div className="aspect-square rounded-xl overflow-hidden bg-stone-100 shadow-sm group-hover:shadow-xl transition-all">
                    {imgSrc ? (
                      <img 
                        src={imgSrc} 
                        alt={product.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-stone-100 to-stone-200">
                        <ImageIcon size={48} className="text-stone-300" />
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <p className="font-medium text-stone-800 truncate group-hover:text-amber-700 transition-colors">
                      {product.title}
                    </p>
                    {product.collection_name && (
                      <p className="text-xs text-stone-500 mt-1">{product.collection_name}</p>
                    )}
                    <p className="text-stone-600 mt-1"><Price amountEur={product.price} /></p>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 bg-stone-50 rounded-2xl">
            <ImageIcon size={48} className="mx-auto mb-4 text-stone-300" />
            <p className="text-stone-500 mb-4">No wallpapers available yet</p>
            <Link to="/ai-product-creator">
              <Button className="rounded-full bg-amber-600 hover:bg-amber-700">
                <Sparkles size={16} className="mr-2" /> Create Your First Wallpaper
              </Button>
            </Link>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="bg-stone-900 py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Create Your Own Wallpaper
          </h2>
          <p className="text-stone-400 mb-8 max-w-xl mx-auto">
            Use our AI-powered creator to design and sell your own premium wallpaper collections.
          </p>
          <Link to="/ai-product-creator">
            <Button className="rounded-full h-12 px-8 bg-amber-500 hover:bg-amber-600 text-stone-900">
              <Sparkles size={16} className="mr-2" /> Start Creating
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
